import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { CheckCircle, Info, Warning, WarningOctagon, X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { ToastContext, type ToastApi, type ToastInput, type ToastTone } from './toastContext'
import { currentLocale, translate } from '@/i18n'

interface ToastItem extends ToastInput {
  id: string
}

const icons: Record<ToastTone, ReactNode> = {
  info: <Info weight="bold" className="text-info" />,
  success: <CheckCircle weight="bold" className="text-ok" />,
  warn: <Warning weight="bold" className="text-warn" />,
  error: <WarningOctagon weight="bold" className="text-danger" />,
}

let counter = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setItems((list) => list.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
  }, [])

  const show = useCallback(
    (input: ToastInput) => {
      const id = `t${++counter}`
      const duration = input.duration ?? (input.tone === 'error' ? 7000 : 4000)
      setItems((list) => [...list.slice(-3), { ...input, id }])
      if (duration > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        )
      return id
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, tone: 'success' }),
      error: (title, description) => show({ title, description, tone: 'error' }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-x-0 bottom-[calc(76px+var(--safe-bottom))] z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:items-end"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className="pointer-events-auto flex w-full max-w-sm animate-toast-in items-start gap-3 rounded-card border border-rule bg-surface px-3.5 py-3 shadow-pop"
          >
            <span className="mt-0.5 text-[18px]" aria-hidden="true">
              {icons[t.tone ?? 'info']}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-ink">{t.title}</div>
              {t.description && (
                <div className="mt-0.5 text-[13px] text-muted">{t.description}</div>
              )}
              {t.action && (
                <button
                  type="button"
                  className="mt-2 font-mono text-[12px] font-bold uppercase tracking-label text-amber-dark hover:underline"
                  onClick={() => {
                    t.action!.onClick()
                    dismiss(t.id)
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              aria-label={translate(currentLocale(), 'a11y.dismiss')}
              onClick={() => dismiss(t.id)}
              className={cn('-mr-1 rounded-chip p-1 text-muted hover:text-ink')}
            >
              <X aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
